// eCommerce Catalog data + mutations (PRD §4.12). Products are read from
// tenants/{t}/products (members read); create/update/delete go through the
// owner/admin-guarded productSave / productDelete callables.
import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { useTenant } from './tenant';

import { call } from './callable';

export interface ProductMetaSync {
  status: 'synced' | 'failed' | 'not_synced' | string;
  error?: string | null;
  at?: number | null;
}

export interface Product {
  id: string;
  name: string;
  description: string | null;
  price: number;
  currency: string;
  imageUrl: string | null;
  sku: string | null;
  inStock: boolean;
  createdAt: number;
  metaSync?: ProductMetaSync | null;
}

export function useProducts(): { products: Product[]; loading: boolean } {
  const { tenantId } = useTenant();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) { setProducts([]); setLoading(false); return; }
    const q = query(collection(db, `tenants/${tenantId}/products`), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      setProducts(snap.docs.map((d) => {
        const p = d.data() as Partial<Product> & { metaSync?: ProductMetaSync | null };
        return {
          id: d.id,
          name: p.name ?? '',
          description: p.description ?? null,
          price: p.price ?? 0,
          currency: p.currency ?? 'OMR',
          imageUrl: p.imageUrl ?? null,
          sku: p.sku ?? null,
          inStock: p.inStock ?? true,
          createdAt: p.createdAt ?? 0,
          metaSync: p.metaSync ?? null,
        };
      }));
      setLoading(false);
    }, () => setLoading(false));
  }, [tenantId]);

  return { products, loading };
}

export interface ProductInput {
  id?: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  imageUrl?: string;
  sku?: string;
  inStock: boolean;
}

const saveFn = httpsCallable<ProductInput, { ok: boolean; id: string }>(functions, 'productSave');
const deleteFn = httpsCallable<{ id: string }, { ok: boolean }>(functions, 'productDelete');

export function saveProduct(input: ProductInput) { return saveFn(input).then((r) => r.data); }
export function deleteProduct(id: string) { return deleteFn({ id }).then((r) => r.data); }

// WP-21b / WP-22: Meta Commerce Catalog Sync Callables
const _catalogConnect = call<
  { integrationId?: string; catalogId: string },
  { ok: boolean; catalogId: string; catalogName: string | null }
>('catalogConnect');

const _catalogDisconnect = call<
  { integrationId?: string },
  { ok: boolean }
>('catalogDisconnect');

const _catalogSyncAll = call<
  { integrationId?: string },
  { ok: boolean; total: number; synced: number; failed: number }
>('catalogSyncAll');

export async function connectCatalog(input: { integrationId?: string; catalogId: string }) {
  const res = await _catalogConnect(input);
  return res.data;
}

export async function disconnectCatalog(input: { integrationId?: string }) {
  const res = await _catalogDisconnect(input);
  return res.data;
}

export async function syncAllCatalogProducts(input: { integrationId?: string } = {}) {
  const res = await _catalogSyncAll(input);
  return res.data;
}

/** Formatted WhatsApp message for a product (share text). */
export function productMessage(p: Product): string {
  const price = `${p.price} ${p.currency}`;
  return [
    `*${p.name}* — ${price}`,
    p.description ?? '',
    p.sku ? `SKU: ${p.sku}` : '',
    p.imageUrl ?? '',
  ].filter(Boolean).join('\n');
}
